import { useState } from 'react';
import { useApplyOverrideMutation, useClearOverrideMutation } from '../../api/mutations';
import { OverrideCountdown } from './OverrideCountdown';
import type { GroupName, OverrideKind } from '../../domain';

const KINDS: readonly OverrideKind[] = ['force_open', 'force_closed', 'hold_at_setpoint'] as const;
const DURATIONS = [
  { ms: 30 * 60_000,           label: '30 min' },
  { ms: 2 * 60 * 60_000,       label: '2 h' },
  { ms: 4 * 60 * 60_000,       label: '4 h' },
  { ms: 12 * 60 * 60_000,      label: '12 h' },
  { ms: 24 * 60 * 60_000,      label: '24 h (max)' },
] as const;

const DEFAULT_DURATION_MS = 4 * 60 * 60_000;

interface ActiveOverride { kind: string; expires_at: string; }

export function OverrideControl({ group, active }: { group: GroupName; active: ActiveOverride | null }) {
  const apply = useApplyOverrideMutation();
  const clear = useClearOverrideMutation();
  const [kind, setKind] = useState<OverrideKind>('force_closed');
  const [durationMs, setDurationMs] = useState<number>(DEFAULT_DURATION_MS);

  if (active) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', marginTop: 'var(--sp-2)' }}>
        <span className="label">Override</span>
        <span className="tag heat">{active.kind}</span>
        <OverrideCountdown expiresAt={active.expires_at} />
        <button className="danger" disabled={clear.isPending} onClick={() => clear.mutate(group)}>
          {clear.isPending ? 'Clearing…' : 'Clear override'}
        </button>
        {clear.isError && <span className="field-error">{(clear.error as Error).message}</span>}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--sp-2)', marginTop: 'var(--sp-2)' }}>
      <label className="label" htmlFor={`ov-kind-${group}`}>Kind</label>
      <select id={`ov-kind-${group}`} value={kind} onChange={(e) => setKind(e.target.value as OverrideKind)}>
        {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
      </select>
      <label className="label" htmlFor={`ov-dur-${group}`}>Duration</label>
      <select id={`ov-dur-${group}`} value={durationMs} onChange={(e) => setDurationMs(Number(e.target.value))}>
        {DURATIONS.map((d) => <option key={d.ms} value={d.ms}>{d.label}</option>)}
      </select>
      <button disabled={apply.isPending} onClick={() => apply.mutate({ group, kind, duration_ms: durationMs })}>
        {apply.isPending ? 'Applying…' : 'Apply'}
      </button>
      {apply.isError && <span className="field-error">{(apply.error as Error).message}</span>}
    </div>
  );
}
